// progress-update.ts — handleProgressUpdate is the WS dispatcher target for
// 'progress_update' messages. Updates [data-progress-id] hosts in the DOM
// (progress bar width + counter + label + detail). 9th and final WS carve-out
// (2026-05-23) — completes the WSMessage sweep at 15/15 typed arms.
//
// PHP publisher: includes/functions/progress/progress_publish_inc.php emits
// { type: 'progress_update', progress_id, current, total, label?, detail? }.
//
// External callers (preserved via window-attach at bottom):
//   - includes/js-src/websocket/router.ts: dispatcher case (imports directly)
//   - Anywhere in template.js that may schedule progress updates would call
//     handleProgressUpdate via bare identifier — resolved via window.

import type { WSMessage } from './types';

type WSProgressUpdateMessage = Extract<WSMessage, { type: 'progress_update' }>;

/**
 * Update the matching ft-progress-host element when a `progress_update` WS
 * event arrives. Called from handleWebSocketMessage() — never directly.
 *
 * Expected payload shape (server-side: progress_publish_inc.php):
 *   { type: 'progress_update', progress_id, current, total, label?, detail? }
 */
function handleProgressUpdate(data: WSProgressUpdateMessage): void {
    const id = data?.progress_id;
    if (!id) return;

    let selector: string;
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        selector = `[data-progress-id="${CSS.escape(id)}"]`;
    } else {
        // Conservative fallback: only allow UUID-ish ids through if CSS.escape is missing.
        if (!/^[0-9A-Za-z\-]+$/.test(id)) return;
        selector = `[data-progress-id="${id}"]`;
    }

    const host = document.querySelector<HTMLElement>(selector);
    if (!host) return;

    const total = Math.max(0, parseInt(String(data.total ?? ''), 10) || 0);
    const current = Math.max(0, parseInt(String(data.current ?? ''), 10) || 0);
    const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : null;

    const bar = host.querySelector<HTMLElement>('.progress-bar');
    const label = host.querySelector<HTMLElement>('.ft-progress-label');
    const counter = host.querySelector<HTMLElement>('.ft-progress-counter');
    const detail = host.querySelector<HTMLElement>('.ft-progress-detail');

    if (bar && pct !== null) {
        bar.style.width = pct + '%';
        bar.setAttribute('aria-valuenow', String(pct));
        // Switch from indeterminate to determinate the moment we have real data.
        bar.classList.remove('progress-bar-animated');
    }
    if (label && data.label) {
        label.textContent = data.label;
    }
    if (counter && total > 0) {
        counter.textContent = `${current} / ${total} (${pct}%)`;
    }
    if (detail) {
        detail.textContent = data.detail ? String(data.detail) : '';
    }
}

window.handleProgressUpdate = handleProgressUpdate;

export { handleProgressUpdate };
